import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ScrollText, X } from "lucide-react";

// Staff + hosts only: "a new policy was added" card, once per new policy version.
export default function PolicyNoticePopup() {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [notices, setNotices] = useState([]);

  const fetchNotices = useCallback(async () => {
    if (!user) { setNotices([]); return; }
    try {
      const { data } = await api.get("/policies/notices");
      setNotices(data.notices || []);
    } catch { setNotices([]); }
  }, [user]);

  useEffect(() => {
    fetchNotices();
    const t = setInterval(fetchNotices, 120000);
    return () => clearInterval(t);
  }, [fetchNotices]);

  if (!notices.length || loc.pathname.startsWith("/policies")) return null;

  const close = async (open) => {
    const ids = notices.map((n) => n.id);
    setNotices([]);
    api.post("/policies/notices/dismiss", { rule_ids: ids }).catch(() => {});
    if (open) nav(`/policies#rule-${ids[0]}`);
  };

  const updates = notices.filter((n) => n.is_update).length;
  return (
    <div className="fixed bottom-6 left-6 z-[60] w-[min(380px,calc(100vw-3rem))] bg-[var(--ff-surface)] border border-[var(--ff-red)] shadow-2xl animate-in slide-in-from-bottom-4 fade-in duration-300" data-testid="policy-notice-popup">
      <div className="h-1 bg-[var(--ff-red)]" />
      <div className="p-5">
        <div className="flex items-start gap-3">
          <ScrollText size={20} className="text-[var(--ff-red)] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow text-[var(--ff-red)] mb-1">
              {notices.length === 1 ? (updates ? "POLICY UPDATED" : "NEW POLICY ADDED") : `${notices.length} POLICY CHANGES`}
            </div>
            <ul className="space-y-1 mb-4" data-testid="policy-notice-list">
              {notices.slice(0, 3).map((n) => (
                <li key={n.id} className="text-sm leading-snug">
                  <span className="font-display">{n.title}</span>
                  {n.section && <span className="text-[var(--ff-text-dim)] text-xs"> · {n.section}</span>}
                </li>
              ))}
              {notices.length > 3 && <li className="text-xs text-[var(--ff-text-dim)]">and {notices.length - 3} more…</li>}
            </ul>
            <button onClick={() => close(true)} className="btn-primary text-xs" data-testid="policy-notice-read-btn">Read Policies</button>
          </div>
          <button onClick={() => close(false)} className="text-[var(--ff-text-dim)] hover:text-white" aria-label="Dismiss" data-testid="policy-notice-dismiss-btn"><X size={16} /></button>
        </div>
      </div>
    </div>
  );
}
